import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperClass } from 'swiper';
import 'swiper/css';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageHero from '@/components/PageHero';
import Section from '@/components/Section';
import NavCarousel from '@/components/NavCarousel';
import Arrow from '@/components/Arrow';
import FeatureMediaCard from './components/FeatureMediaCard';
import HighlightCard from './components/HighlightCard';
import TechFeatureRow from './components/TechFeatureRow';
import CaseCard from './components/CaseCard';
import NewsCard from './components/NewsCard';
import HonorCard from './components/HonorCard';
import { HERO, VALUE, HIGHLIGHT, TECH, PRODUCT, CASE, NEWS, HONOR } from './config';
import styles from './index.module.scss';

const NEWS_VISIBLE = 3;
const HONOR_VISIBLE = 4;
const SLIDE_WHEEL = { forceToAxis: true, releaseOnEdges: true };

export default function Home() {
  const [newsSwiper, setNewsSwiper] = useState<SwiperClass | null>(null);
  const [honorSwiper, setHonorSwiper] = useState<SwiperClass | null>(null);

  return (
    <div className={styles.page}>
      <Header />
      <main>
        <PageHero image={HERO.image} centered={false} innerClassName={styles.heroInner}>
          <h1 className={styles.heroKicker}>{HERO.kicker}</h1>
          <h1 className={styles.heroTitle}>{HERO.title}</h1>
        </PageHero>

        <Section title={VALUE.title} subTitle={VALUE.subTitle}>
          <div className={styles.valueList}>
            {VALUE.cards.map((item) => (
              <FeatureMediaCard key={item.title} {...item} />
            ))}
          </div>
        </Section>

        <Section title={HIGHLIGHT.title} subTitle={HIGHLIGHT.subTitle}>
          <div className={styles.highlightGrid}>
            {HIGHLIGHT.cards.map((item) => (
              <HighlightCard key={item.title} {...item} />
            ))}
          </div>
        </Section>

        <Section title={TECH.title} subTitle={TECH.subTitle}>
          <div className={styles.techList}>
            {TECH.rows.map((item) => (
              <TechFeatureRow key={item.title} {...item} />
            ))}
          </div>
        </Section>

        <Section title={PRODUCT.title} subTitle={PRODUCT.subTitle}>
          <NavCarousel className={styles.productNavCarousel} items={PRODUCT.items} />
        </Section>

        <Section title={CASE.title} subTitle={CASE.subTitle}>
          <div className={styles.caseList}>
            {CASE.cards.map((item) => (
              <CaseCard key={item.title} {...item} />
            ))}
          </div>
        </Section>

        <Section title={NEWS.title} subTitle={NEWS.subTitle}>
          <div className={styles.slideRow}>
            <button
              type="button"
              className={styles.slideNav}
              onClick={() => newsSwiper?.slidePrev()}
              aria-label="上一组"
            >
              <Arrow className={styles.slideNavIcon} rotate={180} />
            </button>
            <Swiper
              className={styles.slideSwiper}
              modules={[Mousewheel]}
              slidesPerView={NEWS_VISIBLE}
              slidesPerGroup={NEWS_VISIBLE}
              speed={2200}
              loop
              loopAddBlankSlides={false}
              allowTouchMove
              mousewheel={SLIDE_WHEEL}
              onSwiper={setNewsSwiper}
            >
              {NEWS.cards.map((item) => (
                <SwiperSlide key={item.href || item.cover} className={styles.newsSlide}>
                  <NewsCard {...item} />
                </SwiperSlide>
              ))}
            </Swiper>
            <button
              type="button"
              className={styles.slideNav}
              onClick={() => newsSwiper?.slideNext()}
              aria-label="下一组"
            >
              <Arrow className={styles.slideNavIcon} />
            </button>
          </div>
        </Section>

        <Section title={HONOR.title} subTitle={HONOR.subTitle}>
          <div className={styles.slideRow}>
            <button
              type="button"
              className={styles.slideNav}
              onClick={() => honorSwiper?.slidePrev()}
              aria-label="上一张"
            >
              <Arrow className={styles.slideNavIcon} rotate={180} />
            </button>
            <Swiper
              className={styles.slideSwiper}
              modules={[Mousewheel]}
              slidesPerView={HONOR_VISIBLE}
              slidesPerGroup={1}
              speed={800}
              loop
              allowTouchMove
              mousewheel={SLIDE_WHEEL}
              onSwiper={setHonorSwiper}
            >
              {HONOR.cards.map((item) => (
                <SwiperSlide key={item.id} className={styles.honorSlide}>
                  <HonorCard {...item} />
                </SwiperSlide>
              ))}
            </Swiper>
            <button
              type="button"
              className={styles.slideNav}
              onClick={() => honorSwiper?.slideNext()}
              aria-label="下一张"
            >
              <Arrow className={styles.slideNavIcon} />
            </button>
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
